import {
	adaptAttempt,
	adaptCompletedResult,
	adaptGameConfig,
	adaptLeaderboardRow,
	adaptPlayer,
	adaptValidationResult,
} from './adapters';
import type {
	ApiErrorPayload,
	Attempt,
	CompletedResult,
	GameConfig,
	LeaderboardRow,
	Player,
	ValidationResult,
} from './types';

const API_ROOT = '/rest/challenge/trivia';

export class ApiError extends Error {
	status: number;
	code: string;

	constructor(status: number, code: string, message: string) {
		super(message);
		this.name = 'ApiError';
		this.status = status;
		this.code = code;
	}
}

const request = async <T>(url: string, init?: RequestInit): Promise<T> => {
	const response = await fetch(url, {
		...init,
		headers: {
			'Content-Type': 'application/json',
			...(init?.headers ?? {}),
		},
	});
	const payload = await response.json().catch(() => ({})) as T & ApiErrorPayload;
	if (!response.ok) {
		throw new ApiError(response.status, payload.code ?? 'REQUEST_FAILED', payload.error ?? 'The request could not be completed.');
	}
	return payload;
};

export const getGame = async (eventSlug: string, gameSlug: string): Promise<GameConfig> =>
	adaptGameConfig(await request(`${API_ROOT}/games/${encodeURIComponent(eventSlug)}/${encodeURIComponent(gameSlug)}`));

export const createPlayer = (
	gameId: number,
	payload: Record<string, unknown>,
): Promise<Player> => request(`${API_ROOT}/games/${gameId}/players`, {
	method: 'POST',
	body: JSON.stringify(payload),
}).then(adaptPlayer);

export const lookupPlayer = (
	gameId: number,
	payload: Record<string, unknown>,
): Promise<{ registered: boolean }> => request<{ registered?: unknown }>(`${API_ROOT}/games/${gameId}/players/lookup`, {
	method: 'POST',
	body: JSON.stringify(payload),
}).then((result) => ({ registered: result.registered === true || result.registered === 1 }));

export const saveSurvey = (
	gameId: number,
	playerId: number,
	responses: Record<string, unknown>,
): Promise<{ id: number | null }> => request<{ id?: unknown }>(`${API_ROOT}/games/${gameId}/surveys`, {
	method: 'POST',
	body: JSON.stringify({ playerId, responses }),
}).then((payload) => ({
	id: payload.id == null || payload.id === '' ? null : Number(payload.id),
}));

export const createAttempt = (
	gameId: number,
	playerId: number,
	surveyResponseId?: number | null,
): Promise<Attempt> => request(`${API_ROOT}/games/${gameId}/attempts`, {
	method: 'POST',
	body: JSON.stringify({ playerId, surveyResponseId }),
}).then(adaptAttempt);

export const validateAnswer = (
	attemptId: number,
	payload: Record<string, unknown>,
): Promise<ValidationResult> => request(`${API_ROOT}/attempts/${attemptId}/answers/validate`, {
	method: 'POST',
	body: JSON.stringify(payload),
}).then(adaptValidationResult);

export const completeAttempt = (
	attemptId: number,
	payload: Record<string, unknown>,
): Promise<CompletedResult> => request(`${API_ROOT}/attempts/${attemptId}/complete`, {
	method: 'POST',
	body: JSON.stringify(payload),
}).then(adaptCompletedResult);

export const getLeaderboard = async (gameId: number): Promise<LeaderboardRow[]> => {
	const payload = await request<{ results?: unknown[] }>(`${API_ROOT}/games/${gameId}/leaderboard?limit=100`);
	return Array.isArray(payload.results) ? payload.results.map(adaptLeaderboardRow) : [];
};
